import { EventRepeatOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import { SystemDataGrid } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { INTEGRATION_SUMMARY } from "../../../../../theme/tokens";
import { formatTimeRange } from "../../../../../utils/dateUtils";
import type { MissingRecurringPatternRow } from "../../../../../utils/models";
import { useIntegrationShiftsRecurringPatterns } from "../integrationSummaryHooks";
import { displayNullable, formatRatio, getSummaryErrorKey, gridSx, weekdayNameFromSundayIndex } from "./consts";
import { EmptySummaryState } from "./EmptySummaryState";
import { SectionHeading, sectionHeadingCountProps } from "./SectionHeading";

const TEST_ID = "hris-shifts-summary-recurring-patterns";
const PATTERN_TIME_FORMAT = "HH:mm";
const MINUTES_PER_HOUR = 60;

/** Current-state check with its own request: it is not scoped by the dialog's date range. */
export const MissingRecurringPatternsGrid: FC = () => {
	const { t, i18n } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { department_id: departmentId, timezone } = chosenDepartment;
	const query = useIntegrationShiftsRecurringPatterns(departmentId, true);
	const data = query.data;
	const errorKey = getSummaryErrorKey(query.error);
	const columns = useMemo<GridColDef<MissingRecurringPatternRow>[]>(() => {
		// Pattern times are department-local wall-clock strings, not instants.
		const parseTime = (value: string) => DateTime.fromFormat(value, PATTERN_TIME_FORMAT, { zone: timezone });
		return [
			{ field: "fullName", headerName: t("integration.summary.column.employee"), flex: 1, minWidth: 150 },
			{
				field: "jobName",
				headerName: t("integration.summary.column.job"),
				flex: 0.8,
				minWidth: 110,
				valueGetter: (_value, row) => displayNullable(row.jobName),
			},
			{
				field: "week",
				headerName: t("integration.summary.shifts.column.week"),
				width: 90,
				valueFormatter: (_value, row) => t("integration.summary.shifts.week.value", { week: row.week }),
			},
			{
				field: "weekday",
				headerName: t("integration.summary.shifts.column.weekday"),
				width: 120,
				valueFormatter: (_value, row) => weekdayNameFromSundayIndex(row.weekday),
			},
			{
				field: "startTime",
				headerName: t("integration.summary.shifts.column.shift"),
				flex: 0.9,
				minWidth: 160,
				// Sort by start time of day; display the range.
				valueGetter: (_value, row) => {
					const start = parseTime(row.startTime);
					return start.hour * MINUTES_PER_HOUR + start.minute;
				},
				valueFormatter: (_value, row) => formatTimeRange(parseTime(row.startTime), parseTime(row.endTime)),
			},
			{
				field: "location",
				headerName: t("integration.summary.shifts.column.location"),
				flex: 0.8,
				minWidth: 110,
				valueGetter: (_value, row) => displayNullable(row.location),
			},
			{
				field: "occurrences",
				headerName: t("integration.summary.shifts.column.occurrences"),
				width: 120,
				valueFormatter: (_value, row) => t("integration.summary.shifts.occurrences.value", { occurrences: row.occurrences, possible: row.possible }),
			},
			{
				field: "confidence",
				headerName: t("integration.summary.shifts.column.confidence"),
				width: 110,
				valueFormatter: (_value, row) => formatRatio(row.confidence, i18n.language),
			},
		];
	}, [i18n.language, t, timezone]);
	return (
		<>
			{data ? (
				<SectionHeading
					title={t("integration.summary.shifts.patterns.title")}
					description={t("integration.summary.shifts.patterns.description")}
					{...sectionHeadingCountProps(data.patterns)}
				/>
			) : (
				<SectionHeading title={t("integration.summary.shifts.patterns.title")} description={t("integration.summary.shifts.patterns.description")} />
			)}
			{data && (
				<InlineHint tone="neutral" icon={<EventRepeatOutlined />} dataTestId={`${TEST_ID}-range`}>
					{t("integration.summary.shifts.patterns.range", {
						start: data.rangeStart.toLocaleString(DateTime.DATE_MED),
						end: data.rangeEnd.toLocaleString(DateTime.DATE_MED),
						confidence: formatRatio(data.minConfidence, i18n.language),
					})}
				</InlineHint>
			)}
			{data && !data.weekAnchorSet && (
				<InlineHint tone="warning" dataTestId={`${TEST_ID}-no-week-anchor`}>
					{t("integration.summary.shifts.patterns.noWeekAnchor")}
				</InlineHint>
			)}
			<Box sx={gridSx} data-testid={`${TEST_ID}-container`}>
				<SystemDataGrid<MissingRecurringPatternRow>
					rows={data ? data.patterns.rows : []}
					columns={columns}
					getRowId={(row) => `${row.nurseId}-${row.week}-${row.weekday}-${row.startTime}-${row.endTime}-${row.location}`}
					searchableColumns={["fullName"]}
					dataTestId={TEST_ID}
					containedHeight={{ maxHeight: INTEGRATION_SUMMARY.GRID_HEIGHT }}
					loading={query.isLoading}
					error={query.isError}
					// DEFAULT: when the backend supplies no user-visible reason, show this request's catch-all
					// retry message; a specific reason (e.g. invalid First Sunday) takes precedence.
					errorMessage={t(errorKey ? errorKey : "integration.summary.shifts.patterns.load.failed")}
					emptyState={
						data ? (
							<EmptySummaryState
								title={t("integration.summary.shifts.patterns.empty.title")}
								hint={t("integration.summary.shifts.patterns.empty.hint", {
									confidence: formatRatio(data.minConfidence, i18n.language),
								})}
								testId={`${TEST_ID}-empty`}
							/>
						) : undefined
					}
				/>
			</Box>
		</>
	);
};
